import { useEffect, useMemo, useCallback } from 'react';
import { useSnapshot, type Snapshot } from 'valtio';
import { createDocumentStore } from '../store';
import { DEFAULT_VISIBILITY_TYPES, type OrganizationParameters } from '#types/System.Parameters.types';
import { type VisibilityType } from '#types/System.types';
import type { LabelSet } from '#types';
import { ensureDefaultVisibilityTypes } from './ensureDefaultVisibilityTypes';
import { ensureOrgParametersExists } from './ensureOrgParametersExists';

interface UseOrganizationParametersOptions {
    enabled?: boolean;
}

type LegacyVisibilityOption = {
    value: string;
    shortLabel: string;
    longDescription?: string;
    canIncludeInternalOnly?: boolean;
    isPdfExport?: boolean;
    id?: string;
    icon?: string;
    internalVisibility?: boolean;
};

const defaultIconForVisibilityTypeId = (id: string): string => {
    if (id === 'internal') return 'EyeOutlined';
    if (id === 'pdfview') return 'GlobalOutlined';
    if (id === 'optimizer') return 'ThunderboltOutlined';
    return 'EyeOutlined';
};

const toVisibilityType = (raw: unknown): VisibilityType | null => {
    if (!raw || typeof raw !== 'object') return null;

    if ('id' in raw && typeof (raw as { id: unknown }).id === 'string') {
        const r = raw as { id: string; shortLabel?: unknown; icon?: unknown; longDescription?: unknown; internalVisibility?: unknown; isPdfExport?: unknown; hideKitComponents?: unknown; hideGroupComponents?: unknown };
        const shortLabel = r.shortLabel;
        const iconRaw = r.icon;

        if (typeof shortLabel !== 'string' || shortLabel.length === 0) return null;
        const icon = typeof iconRaw === 'string' && iconRaw.length > 0 ? iconRaw : defaultIconForVisibilityTypeId(r.id);

        return {
            id: r.id,
            shortLabel,
            longDescription: typeof r.longDescription === 'string' ? r.longDescription : undefined,
            icon,
            internalVisibility: typeof r.internalVisibility === 'boolean' ? r.internalVisibility : undefined,
            isPdfExport: typeof r.isPdfExport === 'boolean' ? r.isPdfExport : undefined,
            hideKitComponents: typeof r.hideKitComponents === 'boolean' ? r.hideKitComponents : undefined,
            hideGroupComponents: typeof r.hideGroupComponents === 'boolean' ? r.hideGroupComponents : undefined,
        };
    }

    if ('value' in raw && typeof (raw as { value: unknown }).value === 'string' && 'shortLabel' in raw && typeof (raw as { shortLabel: unknown }).shortLabel === 'string') {
        const legacy = raw as LegacyVisibilityOption;
        const id = legacy.id ?? legacy.value;
        return {
            id,
            shortLabel: legacy.shortLabel,
            longDescription: legacy.longDescription,
            icon: legacy.icon ?? defaultIconForVisibilityTypeId(id),
            internalVisibility: legacy.internalVisibility ?? (legacy.canIncludeInternalOnly ?? true),
            isPdfExport: legacy.isPdfExport ?? (id === 'pdfview' ? true : undefined),
        };
    }

    return null;
};

const normalizeVisibilityTypes = (raw: unknown): VisibilityType[] => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map(toVisibilityType)
        .filter((v): v is VisibilityType => Boolean(v));
};

export const orgParametersStore = createDocumentStore<OrganizationParameters & Record<string, unknown>>('parameters/organization');

/**
 * Reads the shared `parameters/organization` document.
 *
 * `error` comes off the store state, never off a `connect({ onError })`
 * callback: the singleton keeps only the first connector's callback, so every
 * later consumer would hold a dead channel and read a failed listener as an
 * absent document. `ensureOrgParametersExists` is deliberately swallowed — its
 * rejection is the *expected* outcome for a non-orgsAdmin user, whom the rules
 * allow to read but not create that document, and routing it into `error` would
 * make every export refuse forever in an org that has no such document.
 */
export const useOrganizationParameters = (options?: UseOrganizationParametersOptions) => {
    const isEnabled = options?.enabled ?? true;

    useEffect(() => {
        if (!isEnabled) return;
        orgParametersStore.connect();
        ensureOrgParametersExists().catch(() => {});
        return () => orgParametersStore.disconnect();
    }, [isEnabled]);

    const snap = useSnapshot(orgParametersStore.state);
    const data = isEnabled ? (snap.data as OrganizationParameters | null) : null;
    const loading = isEnabled ? snap.loading : false;
    const error = isEnabled ? snap.error : null;

    const visibilityOptions = useMemo(() => {
        const normalized = normalizeVisibilityTypes(data?.visibilityTypes);
        return ensureDefaultVisibilityTypes(normalized, DEFAULT_VISIBILITY_TYPES);
    }, [data?.visibilityTypes]);

    const visibilityTypesByFormType = useMemo(() => {
        const raw = data?.visibilityTypesByFormType ?? {};
        const entries = Object.entries(raw);
        const normalizedEntries = entries.map(([formTypeId, value]) => [formTypeId, normalizeVisibilityTypes(value)] as const);
        return Object.fromEntries(normalizedEntries) as Record<string, VisibilityType[]>;
    }, [data?.visibilityTypesByFormType]);

    const getVisibilityTypesForFormType = useCallback((formTypeId: string | undefined): VisibilityType[] => {
        if (!formTypeId) return visibilityOptions;
        const additional = visibilityTypesByFormType[formTypeId] ?? [];
        if (additional.length === 0) return visibilityOptions;

        const seen = new Set(visibilityOptions.map(v => v.id));
        const merged = [...visibilityOptions];
        for (const v of additional) {
            if (seen.has(v.id)) continue;
            seen.add(v.id);
            merged.push(v);
        }
        return merged;
    }, [visibilityOptions, visibilityTypesByFormType]);

    /**
     * Every visibility type the org has defined anywhere — the global list plus the
     * per-form-type lists, first definition of an id wins. Surfaces that edit an
     * object with no form scope (a field type, an ArTable column) must offer this,
     * not `visibilityOptions`: the global list holds only `internal` and `pdfview`
     * in a real org, so scoping those editors to it leaves one selectable mode.
     */
    const allVisibilityTypes = useMemo(() => {
        const byId = new Map(visibilityOptions.map(v => [v.id, v]));
        for (const types of Object.values(visibilityTypesByFormType)) {
            for (const v of types) {
                if (!byId.has(v.id)) byId.set(v.id, v);
            }
        }
        return Array.from(byId.values());
    }, [visibilityOptions, visibilityTypesByFormType]);

    const visibilityMetadata = useMemo(() => {
        return new Map(visibilityOptions.map(opt => [opt.id, opt]));
    }, [visibilityOptions]);

    const pdfExportOptions = useMemo(() => {
        return visibilityOptions.filter(option =>
            option.isPdfExport === true || option.id === 'pdfview'
        );
    }, [visibilityOptions]);

    return {
        data,
        loading,
        error,
        visibilityOptions,
        visibilityTypesByFormType,
        allVisibilityTypes,
        visibilityMetadata,
        pdfExportOptions,
        getVisibilityTypesForFormType,
    };
};

const EMPTY_LABEL_SET: Snapshot<LabelSet> = { groups: {}, groupOrder: [] };

/**
 * One admin-defined label set off the shared organization parameters document.
 *
 * Joining the refcounted store adds a consumer to the listener every page
 * rendering a form already holds, and opens none of its own. The snapshot is
 * read down to the single field, so an unrelated organization parameter
 * changing does not re-render the caller. A tenant that has never defined a
 * label carries no field at all and yields an empty set, never `undefined`.
 */
const useLabelSet = (field: 'attachmentLabels' | 'communicationLogLabels'): Snapshot<LabelSet> => {
    useEffect(() => {
        orgParametersStore.connect();
        return () => orgParametersStore.disconnect();
    }, []);

    const snap = useSnapshot(orgParametersStore.state);
    return snap.data?.[field] ?? EMPTY_LABEL_SET;
};

/**
 * The attachment label vocabulary. Disjoint from the communication log's: an
 * id defined here never resolves there, and neither caller sees the other set.
 */
export const useAttachmentLabels = (): Snapshot<LabelSet> => useLabelSet('attachmentLabels');

/**
 * The communication log label vocabulary. Disjoint from the attachments':
 * an id defined here never resolves there, and neither caller sees the other set.
 */
export const useCommunicationLogLabels = (): Snapshot<LabelSet> => useLabelSet('communicationLogLabels');
